import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, User, GraduationCap, Calendar, School, Globe } from "lucide-react";
import PriorAssessmentHistory from "@/components/PriorAssessmentHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Student detail page — teacher/admin view.
 *
 * Shows a student's basic profile, prior assessment history tile, and a
 * chronological list of their MOSAIC assessment sessions. Linked from
 * dashboards and search results.
 *
 * Route: /students/:studentId   (teacher / school_admin / district_admin / super_admin)
 */
const StudentDetail = () => {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [stuRes, resRes] = await Promise.all([
          axios.get(`${API}/users/${studentId}`),
          axios.get(`${API}/results?student_id=${studentId}`).catch(() => ({ data: { results: [] } })),
        ]);
        if (cancelled) return;
        setStudent(stuRes.data);
        setResults(resRes.data?.results || []);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  if (loading) {
    return <div className="p-10 text-center text-gray-500" data-testid="student-detail-loading">Loading student profile…</div>;
  }
  if (err || !student) {
    return (
      <div className="p-10 text-center" data-testid="student-detail-error">
        <p className="text-red-600 font-semibold mb-2">Could not load student</p>
        <p className="text-gray-500 text-sm">{err || "Student not found"}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="student-detail-page">
      <Link to="/dashboard" className="inline-flex items-center text-sm text-[#002FA7] hover:underline mb-4" data-testid="back-link">
        <ArrowLeft size={16} className="mr-1" /> Back to dashboard
      </Link>

      {/* Student header */}
      <div className="border-2 border-gray-200 p-6 mb-8" data-testid="student-header">
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 bg-[#002FA7]/10 flex items-center justify-center">
            <User size={32} className="text-[#002FA7]" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
              {student.name || student.full_name || student.email || student.user_id}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 text-sm text-gray-600">
              {student.grade_level && (
                <div className="flex items-center gap-2" data-testid="student-grade">
                  <GraduationCap size={16} /> <span>Grade {student.grade_level}</span>
                </div>
              )}
              {student.home_language && (
                <div className="flex items-center gap-2" data-testid="student-home-language">
                  <Globe size={16} /> <span>{student.home_language}</span>
                </div>
              )}
              {student.school_name && (
                <div className="flex items-center gap-2" data-testid="student-school">
                  <School size={16} /> <span>{student.school_name}</span>
                </div>
              )}
              {student.ell_arrival_date && (
                <div className="flex items-center gap-2" data-testid="student-arrival">
                  <Calendar size={16} /> <span>Arrived {student.ell_arrival_date}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Prior Assessment History */}
      <PriorAssessmentHistory studentId={studentId} />

      {/* MOSAIC assessment sessions */}
      <div className="border-2 border-gray-200 mb-8" data-testid="mosaic-sessions">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
            MOSAIC Assessment Sessions
          </h3>
          <p className="text-sm text-gray-500 mt-1">Chronological list of completed MOSAIC assessments</p>
        </div>
        {results.length === 0 ? (
          <div className="p-6 text-sm text-gray-500" data-testid="no-sessions">
            No completed MOSAIC assessments on file yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {results.map((r) => (
              <Link
                key={r.exam_session_id || r.id}
                to={`/results/${r.exam_session_id || r.id}`}
                className="flex items-center justify-between p-5 hover:bg-gray-50"
                data-testid={`session-row-${r.exam_session_id || r.id}`}
              >
                <div>
                  <div className="font-semibold">
                    Grade {r.grade_level} · {r.scored_at ? new Date(r.scored_at).toLocaleDateString() : "—"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Session {r.exam_session_id || r.id}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {r.wida_level || "—"}
                  </div>
                  <div className="text-xs text-gray-500">{r.wida_level_name || ""}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetail;
